import React, { useState, useEffect } from "react";
import { useRFQStream } from "../hooks/useRFQStream";
import { QuoteNotification, useNotifications } from "./QuoteNotification";
import { useSettings } from "../context/SettingsContext";
import { formatTime, formatDate } from "../utils/dateUtils";

/**
 * RFQStream component handles real-time streaming of dealer quotes and counters
 * for an active RFQ request.
 */
export default function RFQStream({ rfqId, onClose, onOpenChat }) {
  const { playQuoteSound } = useSettings();
  const {
    quotes,
    wsStatus,
    error,
    rfqStatus,
    acceptQuote,
    rejectQuote,
    sendCounterOffer,
  } = useRFQStream(rfqId);

  const { notifications, addNotification, removeNotification } = useNotifications();
  const [prevQuotesLength, setPrevQuotesLength] = useState(0);

  // Show notification for new quotes and play sound
  useEffect(() => {
    if (quotes.length > prevQuotesLength) {
      const newQuote = quotes[quotes.length - 1];
      // Include rfqId on the notification so click handlers can open relevant chat
      addNotification({ quote: newQuote, rfqId });
      
      // Play quote sound
      playQuoteSound();
    }
    setPrevQuotesLength(quotes.length);
  }, [quotes.length, playQuoteSound]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col h-full relative">
      {/* Notifications */}
      {notifications.map((notif) => (
        <QuoteNotification
          key={notif.id}
          quote={notif.quote}
          onDismiss={() => removeNotification(notif.id)}
          onOpenChat={(quote) => {
            // If parent provided an onOpenChat handler, call with RFQ context
            if (onOpenChat) {
              try {
                onOpenChat({
                  rfqNumber: notif.rfqId,
                  dealerName: quote?.dealerName || quote?.dealer || 'Company',
                  dealerId: quote?.dealerId || null,
                  material: quote?.material || null,
                });
              } catch (err) {
                console.error('[RFQStream] onOpenChat handler error:', err);
              }
            }
            // Always emit a global event so top-level app can respond without prop threading
            try {
              const detail = {
                rfqNumber: notif.rfqId,
                dealerName: quote?.dealerName || quote?.dealer || 'Company',
                dealerId: quote?.dealerId || null,
                material: quote?.material || null,
              };
              window.dispatchEvent(new CustomEvent('open-chat', { detail }));
            } catch (err) {
              console.warn('[RFQStream] Failed to dispatch open-chat event:', err);
            }
          }}
        />
      ))}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-blue-600 text-white">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Live Quotes - RFQ #{rfqId}</h3>
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                wsStatus === "connected"
                  ? "bg-green-400"
                  : wsStatus === "connecting"
                  ? "bg-yellow-400 animate-pulse"
                  : "bg-red-400"
              }`}
            />
            <span className="text-xs capitalize">{wsStatus}</span>
          </div>
          {rfqStatus === "complete" && (
            <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded">
              Complete
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-blue-700 rounded transition-colors"
          title="Close stream"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-300 rounded text-red-700 text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Quotes List */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {quotes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p>Waiting for dealer quotes...</p>
              <p className="text-sm mt-1">Dealers will respond shortly</p>
            </div>
          </div>
        ) : (
          quotes
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
            .map((quote) => (
              <QuoteCard
                key={quote.id}
                quote={quote}
                onAccept={acceptQuote}
                onReject={rejectQuote}
                onCounter={sendCounterOffer}
              />
            ))
        )}
      </div>
    </div>
  );
}

function QuoteCard({ quote, onAccept, onReject, onCounter }) {
  const [showCounterInput, setShowCounterInput] = useState(false);
  const [counterPrice, setCounterPrice] = useState("");

  const handleCounter = () => {
    const price = parseFloat(counterPrice);
    if (!isNaN(price) && price > 0) {
      onCounter(quote.dealerId, quote.id, price);
      setShowCounterInput(false);
      setCounterPrice("");
    }
  };

  const isCounter = quote.type === "counter";
  const isAccepted = quote.status === "accepted";
  const isRejected = quote.status === "rejected";

  return (
    <div
      className={`border rounded-lg p-4 transition-all ${
        isAccepted
          ? "bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-700"
          : isRejected
          ? "bg-gray-50 dark:bg-gray-800 border-gray-300 dark:border-gray-700 opacity-60"
          : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 shadow-sm hover:shadow-md"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-lg text-gray-900 dark:text-gray-100">{quote.dealerName}</h4>
          <div className="flex items-center gap-2 mt-1">
            {isCounter && (
              <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 text-xs font-medium rounded">
                Counter Offer
              </span>
            )}
            {isAccepted && (
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-medium rounded">
                Accepted
              </span>
            )}
            {isRejected && (
              <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-medium rounded">
                Rejected
              </span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatTime(quote.timestamp)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            ${quote.price.toLocaleString()}
          </div>
          {quote.pricePerUnit && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              ${quote.pricePerUnit.toFixed(2)}/{quote.unit}
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
        <div>
          <span className="text-gray-600 dark:text-gray-400">Quantity:</span>
          <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
            {quote.quantity} {quote.unit}
          </span>
        </div>
        {quote.validUntil && (
          <div>
            <span className="text-gray-600 dark:text-gray-400">Valid Until:</span>
            <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
              {formatDate(quote.validUntil)}
            </span>
          </div>
        )}
      </div>

      {/* Notes */}
      {quote.notes && (
        <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 p-2 rounded mb-3">
          <strong>Notes:</strong> {quote.notes}
        </div>
      )}

      {/* Actions */}
      {!isAccepted && !isRejected && (
        <div className="flex gap-2">
          <button
            onClick={() => onAccept(quote.id, quote.dealerId)}
            className="flex-1 px-4 py-2 bg-green-600 dark:bg-green-700 text-white font-medium rounded hover:bg-green-700 dark:hover:bg-green-800 transition-colors"
          >
            Accept
          </button>
          <button
            onClick={() => setShowCounterInput(!showCounterInput)}
            className="flex-1 px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white font-medium rounded hover:bg-orange-700 dark:hover:bg-orange-800 transition-colors"
          >
            Counter
          </button>
          <button
            onClick={() => onReject(quote.id, quote.dealerId)}
            className="flex-1 px-4 py-2 bg-gray-600 dark:bg-gray-700 text-white font-medium rounded hover:bg-gray-700 dark:hover:bg-gray-800 transition-colors"
          >
            Reject
          </button>
        </div>
      )}

      {/* Counter Offer Input */}
      {showCounterInput && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Your Counter Offer:
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={counterPrice}
              onChange={(e) => setCounterPrice(e.target.value)}
              placeholder="Enter price..."
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={handleCounter}
              className="px-4 py-2 bg-orange-600 dark:bg-orange-700 text-white font-medium rounded hover:bg-orange-700 dark:hover:bg-orange-800 transition-colors"
            >
              Send
            </button>
            <button
              onClick={() => {
                setShowCounterInput(false);
                setCounterPrice("");
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded hover:bg-gray-400 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
